<script setup>
import { ref, computed, h } from 'vue'
import {
  useVueTable,
  getCoreRowModel,
  createColumnHelper,
  FlexRender
} from '@tanstack/vue-table'
import { IP_TABLE_COLUMNS, getColumnConfig } from '@/config/ipTableFields'

// Import widget components
import Country from '@/components/Query/Widget/Country.vue'
import IpWidget from '@/components/Query/Widget/Ip.vue'
import TagsBasic from '@/components/Query/Widget/TagsBasic.vue'
import Netflow from '@/components/Query/Widget/Netflow.vue'
import SubmissionCount from '@/components/Query/Widget/SubmissionCount.vue'
import Anonymization from '@/components/Query/Widget/Anonymization.vue'
import OsintProviders from '@/components/Query/Widget/OsintProviders.vue'
import Tags from '@/components/Query/Widget/Tags.vue'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  enabledFields: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['navigate-ip', 'navigate-event-search'])

// Search filter state
const searchFilter = ref('')

// Sort state
const sortField = ref(null)
const sortDirection = ref(null) // 'asc', 'desc', or null

// Filtered data based on search
const filteredData = computed(() => {
  if (!searchFilter.value.trim()) {
    return props.data
  }

  const query = searchFilter.value.toLowerCase().trim()

  return props.data.filter(row => {
    // Search in IP
    if (row.ip && row.ip.toLowerCase().includes(query)) return true

    // Search in hostnames
    if (row.hostnames && row.hostnames.some(h => h.toLowerCase().includes(query))) return true

    // Search in company name
    if (row.company?.name && row.company.name.toLowerCase().includes(query)) return true

    // Search in network type
    if (row.network?.type && row.network.type.toLowerCase().includes(query)) return true

    // Search in location
    if (row.location) {
      const loc = row.location
      if (Array.isArray(loc) && loc.length > 0) {
        if (loc[0].country_iso?.toLowerCase().includes(query)) return true
        if (loc[0].country_name?.toLowerCase().includes(query)) return true
      } else if (loc.country_iso?.toLowerCase().includes(query)) return true
      else if (loc.country_name?.toLowerCase().includes(query)) return true
    }

    return false
  })
})

// Sorted data (applies sorting to filtered data)
const sortedData = computed(() => {
  if (!sortField.value || !sortDirection.value) {
    return filteredData.value
  }

  const field = sortField.value
  const dir = sortDirection.value

  return [...filteredData.value].sort((a, b) => {
    const aVal = getValue(a, field)
    const bVal = getValue(b, field)

    // Handle null/undefined
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return dir === 'asc' ? -1 : 1
    if (bVal == null) return dir === 'asc' ? 1 : -1

    // Handle arrays (compare first element or length)
    if (Array.isArray(aVal) && Array.isArray(bVal)) {
      const aLen = aVal.length
      const bLen = bVal.length
      return dir === 'asc' ? aLen - bLen : bLen - aLen
    }

    // Handle objects (try to get a sortable value)
    if (typeof aVal === 'object' && typeof bVal === 'object') {
      // For location, sort by country_iso
      const aStr = aVal.country_iso || aVal.name || JSON.stringify(aVal)
      const bStr = bVal.country_iso || bVal.name || JSON.stringify(bVal)
      return dir === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    }

    // Handle numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return dir === 'asc' ? aVal - bVal : bVal - aVal
    }

    // Handle strings
    const aStr = String(aVal).toLowerCase()
    const bStr = String(bVal).toLowerCase()
    return dir === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr)
  })
})

// Sort helper functions
function getSortState(fieldName) {
  if (sortField.value !== fieldName) return null
  return { direction: sortDirection.value }
}

function handleHeaderClick(fieldName) {
  if (sortField.value === fieldName) {
    // Cycle: asc -> desc -> none
    if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc'
    } else if (sortDirection.value === 'desc') {
      sortField.value = null
      sortDirection.value = null
    }
  } else {
    // New field, start with asc
    sortField.value = fieldName
    sortDirection.value = 'asc'
  }
}

// Handle navigation events from widgets
function handleIpNavigation(ip) {
  emit('navigate-ip', ip)
}

function handleEventSearchNavigation(ip) {
  emit('navigate-event-search', ip)
}

// Get nested value from object using dot notation
function getValue(obj, path) {
  if (!path) return obj
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

// Build column helper
const columnHelper = createColumnHelper()

// Build table columns from enabled fields
const tableColumns = computed(() => {
  const fields = props.enabledFields.length > 0
    ? props.enabledFields
    : IP_TABLE_COLUMNS.map(c => c.key)

  return fields.map(fieldKey => {
    const config = getColumnConfig(fieldKey)
    if (!config) {
      // Fallback for unknown fields
      return columnHelper.accessor(row => getValue(row, fieldKey), {
        id: fieldKey,
        header: fieldKey,
        cell: info => info.getValue() ?? ''
      })
    }

    return columnHelper.accessor(row => getValue(row, fieldKey), {
      id: fieldKey,
      header: config.label,
      cell: info => {
        const value = info.getValue()
        const row = info.row.original

        // Return widget component based on config
        switch (config.widget) {
          case 'Country':
            return h(Country, { data: value })
          case 'IpWidget':
            return h(IpWidget, {
              data: value,
              onNavigate: handleIpNavigation
            })
          case 'TagsBasic':
            return h(TagsBasic, { data: value, maxlen: 1, nostyle: true })
          case 'Netflow':
            return h(Netflow, {
              data: value,
              rowData: row,
              onNavigate: handleEventSearchNavigation
            })
          case 'SubmissionCount':
            return h(SubmissionCount, {
              data: value,
              rowData: row,
              onNavigate: handleIpNavigation
            })
          case 'Anonymization':
            return h(Anonymization, { data: value })
          case 'OsintProviders':
            return h(OsintProviders, { data: value })
          case 'Tags':
            return h(Tags, { data: value, maxlen: 3 })
          case 'Text':
          default:
            return value ?? ''
        }
      },
      size: config.width || undefined
    })
  })
})

// Create table instance
const table = useVueTable({
  get data() {
    return sortedData.value
  },
  get columns() {
    return tableColumns.value
  },
  getCoreRowModel: getCoreRowModel(),
  getRowId: (row) => row.ip || String(Math.random())
})

function clearFilter() {
  searchFilter.value = ''
}
</script>

<template>
  <div class="ip-results-table">
    <!-- Empty state (no data at all) -->
    <div v-if="data.length === 0" class="state-overlay">
      <div class="state-box">
        <span class="state-message">No results</span>
      </div>
    </div>

    <!-- Has data -->
    <template v-else>
      <!-- Table Container -->
      <div class="table-container">
        <!-- No matches for filter overlay (inside table container) -->
        <div v-if="filteredData.length === 0" class="filter-empty-overlay">
          <div class="state-box">
            <span class="state-message">No matching results</span>
            <span class="state-hint">Try adjusting your filter</span>
          </div>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :style="{ width: header.getSize() ? `${header.getSize()}px` : 'auto' }"
                  class="sortable-header"
                  @click="handleHeaderClick(header.column.id)"
                >
                  <div class="header-content">
                    <span class="header-label">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                    </span>
                    <span class="sort-indicator" :class="{ 'sort-active': getSortState(header.column.id) }">
                      {{ getSortState(header.column.id)?.direction === 'asc' ? '▲' : '▼' }}
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in table.getRowModel().rows" :key="row.id">
                <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                  <div class="cell-content">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Table Footer (always visible when there's data) -->
      <div class="table-footer">
        <!-- Search Filter (left side) -->
        <div class="footer-left">
          <div class="search-container" :class="{ 'has-value': searchFilter }">
            <font-awesome-icon class="search-icon" :icon="['fas', 'magnifying-glass']" />
            <input
              v-model="searchFilter"
              class="search-input"
              placeholder="Filter results..."
              type="text"
            />
            <button v-if="searchFilter" class="search-clear" @click="clearFilter">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>

        <!-- Result Count (center) -->
        <div class="result-info">
          <span v-if="searchFilter">
            {{ filteredData.length }} of {{ data.length.toLocaleString() }} results
          </span>
          <span v-else>
            {{ data.length.toLocaleString() }} results
          </span>
        </div>

        <!-- Right side placeholder for balance -->
        <div class="footer-right"></div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.ip-results-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #151a22;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

/* State Overlay (when no data at all) */
.state-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 3rem;
  background: #1a1f27;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.state-message {
  color: #E5E7EB;
  font-size: 0.95rem;
  text-align: center;
}

.state-hint {
  color: #6B7280;
  font-size: 0.8rem;
  text-align: center;
}

/* Table Container */
.table-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* Filter empty overlay (contained within table-container) */
.filter-empty-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
}

/* Scrollbar styling */
.table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4A5661 0%, #3E4A56 100%);
  border-radius: 10px;
  border: 2px solid rgba(0, 0, 0, 0.2);
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5A6671 0%, #4E5A66 100%);
}

.table-scroll::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.2);
}

/* Table Styling */
table {
  min-width: 100%;
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

thead th {
  background-color: #151a22;
  color: #9CA3AF;
  text-align: left;
  padding: 1rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Sortable Headers */
thead th.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

thead th.sortable-header:hover {
  background-color: #1d2531;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
}

.header-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sort-indicator {
  color: #F59E0B;
  font-size: 0.65rem;
  flex-shrink: 0;
  margin-left: auto;
  opacity: 0;
  min-width: 0.65rem;
}

.sort-indicator.sort-active {
  opacity: 1;
}

tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #E5E7EB;
  font-size: 0.85rem;
  vertical-align: top;
}

tbody tr {
  transition: background-color 0.15s ease;
}

tbody tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.015);
}

tbody tr:nth-child(odd) {
  background-color: transparent;
}

tbody tr:hover {
  background-color: rgba(115, 87, 210, 0.08) !important;
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Table Footer */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
  background: #151a22;
}

.footer-left,
.footer-right {
  flex: 1;
  min-width: 0;
}

.footer-left {
  display: flex;
  justify-content: flex-start;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

/* Search Container */
.search-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.search-container:focus-within {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(115, 87, 210, 0.4);
}

.search-container.has-value {
  background: rgba(115, 87, 210, 0.1);
  border-color: rgba(115, 87, 210, 0.3);
}

.search-icon {
  color: #6B7280;
  font-size: 0.75rem;
}

.search-input {
  background: transparent;
  border: none;
  outline: none;
  color: #E5E7EB;
  font-size: 0.8rem;
  width: 140px;
}

.search-input::placeholder {
  color: #6B7280;
}

.search-clear {
  background: none;
  border: none;
  color: #6B7280;
  padding: 0.15rem;
  cursor: pointer;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.search-clear:hover {
  color: #E5E7EB;
  background: rgba(255, 255, 255, 0.1);
}

/* Result Info */
.result-info {
  color: #6B7280;
  font-size: 0.8rem;
  flex-shrink: 0;
}
</style>
